import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ethers } from 'ethers';
import { useLocalStorage } from 'usehooks-ts';
import { ACTIVE_NETWORKS, ChainId } from '@shared/utils/config/networks';
import Fuse from 'fuse.js';
import { ModeValue, SubModeValue, TimeFrames } from '@shared/types/frontend/searchHome';
import { useSearchParams } from 'next/navigation';
import { DefaultStages } from '@shared/types/frontend';
import { isAddress } from 'ethers/lib/utils';
import useTrendingTokens from '@/hooks/explore/useTrendingTokens';
import useTokenSearch from '@/hooks/explore/useTokenSearch';
import useTopTokens from '@/hooks/explore/useTopToken';
import { TopTokenAddresses } from '@/hooks/explore/tokenAddresses';
import useGetNativeTokenData from '@/hooks/explore/useGetNativeTokenData';
import validateSolAddress from '@/lib/misc/validateSolAddress';
import { useRecentToken } from '@/hooks/saved/useRecentToken';
import { useSavedToken } from '@/hooks/saved/useSavedToken';
import { useBasicTokenInfoList } from '@/hooks/pricing/useBasicTokenInfo';
import useListedTokenData from '@/hooks/explore/useListedTokenData';
import { useIsMobile } from '@/hooks/utilities/use-mobile';
interface SearchContextInterface {
  handleClearSavedTokens: () => void;
  DefaultStages?: typeof DefaultStages;
  showWildWest?: (bool: boolean) => void;
  wildWest?: boolean;
  handleSearch?: (searchTerm: string) => void;
  keyword?: string;
  setKeyword?: React.Dispatch<React.SetStateAction<string>>;
  network?: number[];
  setNetwork?: React.Dispatch<React.SetStateAction<number[]>>;
  timeFrame?: TimeFrames;
  setTimeFrame?: React.Dispatch<React.SetStateAction<TimeFrames>>;
  loadingModes?: any;
  resultsToUse?: () => any;
  useUnprotectedSearch?: boolean;
  allTokensLoading?: boolean;
  setUseUnprotectedSearch?: React.Dispatch<React.SetStateAction<boolean>>;
  defaultNetwork?: typeof ACTIVE_NETWORKS;
  subMode?: SubModeValue;
  setSubMode?: React.Dispatch<React.SetStateAction<SubModeValue>>;
  subMemeData?: any;
  allTokens?: any;
  mode?: ModeValue;
  setMode?: React.Dispatch<React.SetStateAction<ModeValue>>;
  stage?: DefaultStages;
  setStage?: React.Dispatch<React.SetStateAction<DefaultStages>>;
  recentItems?: any;
  searchBarisFocused?: boolean;
  setSearchBarIsFocused?: React.Dispatch<React.SetStateAction<boolean>>;
}
const SearchContext = createContext({} as SearchContextInterface);
export const useSearchHome = () => {
  const searchParams = useSearchParams();
  const [_paramMode, _paramTimeFrame, _paramNetworkId] = [searchParams.get('mode'), searchParams.get('timeframe'), searchParams.get('networkId')];
  const defaultNetwork = ACTIVE_NETWORKS;
  const [searchBarisFocused, setSearchBarIsFocused] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [wildWest, showWildWest] = useLocalStorage('Wild West-categories', false);
  const [timeFrame, setTimeFrame] = useState<TimeFrames>('24');
  const [network, setNetwork] = useState<number[]>(defaultNetwork);
  const [subMode, setSubMode] = useState<SubModeValue>('Trending');
  const [useUnprotectedSearch, setUseUnprotectedSearch] = useState(false);
  const [mode, setMode] = useState<ModeValue>('');
  const [stage, setStage] = useState(DefaultStages.loading);
  const isMobile = useIsMobile();
  useEffect(() => {
    if (!isMobile) {
      setMode('memes');
    } else {
      setMode('');
    }
  }, [isMobile]);
  useEffect(() => {
    if (_paramMode) {
      setMode(_paramMode as ModeValue);
    }
    // if (_paramNetworkId) {
    //   setNetwork([parseInt(_paramNetworkId)]);
    // }
    if (_paramTimeFrame) {
      setTimeFrame(_paramTimeFrame as TimeFrames);
    }
    return () => {};
    return () => {};
  }, [_paramMode, _paramTimeFrame, _paramNetworkId]);

  // TODO: Refactor debounced search
  const handleSearch = (searchTerm: string) => {
    if (searchTerm === '') return;
    setKeyword(searchTerm);
  };
  useEffect(() => {
    if (keyword !== '') {
      resultsToUse();
    }
    setUseUnprotectedSearch(false);
  }, [keyword]);
  const {
    nativeTokensData,
    nativeTokensDataLoading
  } = useGetNativeTokenData();
  const searchTermIsAddress = () => {
    try {
      if (ethers.utils.isAddress(keyword) || validateSolAddress(keyword, false)) {
        return true;
      }
      return false;
    } catch (_) {
      return false;
    }
  };
  const {
    tokens: trendingResults,
    isLoading: trendingLoading
  } = useTrendingTokens({
    resolution: timeFrame,
    networkFilter: network
  });
  const {
    subMemeData: unfilteredSubMemeData,
    subMemeLoading
  } = useListedTokenData();
  const subMemeData = useMemo(() => {
    if (!unfilteredSubMemeData) return [];
    const filteredMemeNetworkData = unfilteredSubMemeData.filter(x => network?.includes(x.token.networkId));
    if (subMode == 'Trending') {
      return filteredMemeNetworkData;
    } else {
      return filteredMemeNetworkData?.filter(x => x?.category == subMode);
    }
  }, [subMode, unfilteredSubMemeData, network]);
  const {
    data: searchResultsDegen,
    isLoading
  } = useTokenSearch({
    phrase: keyword,
    timeFrame: timeFrame,
    network,
    limit: 100,
    volumeMin: useUnprotectedSearch || searchTermIsAddress() ? 0 : 1000,
    mktCapMin: useUnprotectedSearch || searchTermIsAddress() ? 0 : 10000,
    txnCountMin: 0,
    liquidityMin: useUnprotectedSearch || searchTermIsAddress() ? 0 : 1000,
    minUniqueTransactions: useUnprotectedSearch || searchTermIsAddress() ? 0 : 10,
    identifier: keyword?.length > 1 ? 'search' : null,
    filterStables: false,
    sortSearchResultsViaAlgo: false,
    cacheTime: 0,
    showPotentialScam: useUnprotectedSearch || searchTermIsAddress() ? true : false
  });
  const {
    data: allTokenPrefilter,
    isLoading: allTokensLoading
  } = useTokenSearch({
    timeFrame,
    limit: 100,
    network: network.filter(x => x != ChainId.ETHEREUM && x != ChainId.BSC),
    rankingBy: 'marketCap',
    rankingDirection: 'DESC',
    volumeMin: 100000,
    liquidityMin: 100000,
    txnCountMin: 50,
    addNatives: false,
    sortSearchResultsViaAlgo: false,
    identifier: 'top',
    minAgeMinutes: 60 * 24 * 3,
    filterStables: true
  });
  const allTokens = useMemo(() => {
    return allTokenPrefilter?.filter(x => !x?.token?.name?.toLowerCase()?.includes('wrapped') && !x?.token?.name?.toLowerCase()?.includes('staked') && !x?.token?.name?.toLowerCase()?.includes('sol') && !x?.token?.name?.toLowerCase()?.includes('eth') && !x?.token?.name?.toLowerCase()?.includes('btc'));
  }, [allTokenPrefilter]);

  // TODO: refactor
  const {
    data: topResults,
    isLoading: topLoading
  } = useTopTokens(TopTokenAddresses);
  const fuseData = () => {
    let data = [];
    if (unfilteredSubMemeData) {
      data = unfilteredSubMemeData;
    }
    if (topResults) {
      data = data.concat(topResults);
    }
    if (allTokens) {
      data = data.concat(allTokens);
    }
    if (nativeTokensData) {
      const updatedNativeTokens = nativeTokensData.filter(x => {
        if (x?.token?.symbol == 'ETH' && x.token.networkId != ChainId.BASE) return false;
        return true;
      }).map(x => {
        if (x?.token?.symbol == 'ETH' || x?.token?.symbol == 'BNB' || x?.token?.symbol == 'SOL' || x?.token?.symbol == 'AVAX' || x?.token?.symbol == 'MATIC' || x?.token?.symbol == 'POL') {
          x.marketCap = 'native';
        }
        return {
          ...x
        };
      }).sort((a, _) => a.token.networkId == ChainId.ETHEREUM ? -1 : a.token.networkId == ChainId.SOLANA ? -0.5 : 1);
      data = data.concat(updatedNativeTokens);
    }
    return data?.filter((x, idx, self) => idx === self?.findIndex(t => t?.token?.address === x?.token?.address && t?.token?.networkId === x?.token?.networkId)).filter(x => x?.token?.name != 'Wrapped Ether');
  };
  const fuse = new Fuse(fuseData(), {
    // The algorithms to use for fuzzy matching.
    keys: ['token.name', 'token.symbol'],
    // The minimum similarity score required for a match.
    includeScore: true
  });
  const normieSearchResults = fuse.search(keyword).map(x => x.item);
  const searchResults = wildWest || isAddress(keyword) || validateSolAddress(keyword, false) ? searchResultsDegen : normieSearchResults;
  const {
    data: newTokens,
    isLoading: newLoading
  } = useTokenSearch({
    timeFrame: timeFrame,
    network,
    limit: 100,
    volumeMin: 1000,
    mktCapMin: 5000,
    liquidityMin: 2000,
    minUniqueTransactions: 10,
    identifier: 'newTokens',
    filterStables: false,
    sortSearchResultsViaAlgo: false,
    rankingBy: `trendingScore${timeFrame}`,
    maxAgeMinutes: 60 * 24,
    cacheTime: 1000 * 30
  });
  const {
    data: gainerResults,
    isLoading: gainerLoading
  } = useTokenSearch({
    timeFrame: timeFrame,
    network,
    limit: 100,
    volumeMin: 1000,
    mktCapMin: 5000,
    liquidityMin: 25000,
    minUniqueTransactions: 10,
    txnCountMin: 50,
    identifier: 'gainers',
    filterStables: false,
    sortSearchResultsViaAlgo: false,
    minAgeMinutes: 60 * 1,
    rankingBy: `change${timeFrame}`,
    cacheTime: 1000 * 30
  });
  const {
    data: loserResults,
    isLoading: loserLoading
  } = useTokenSearch({
    timeFrame: timeFrame,
    network,
    limit: 100,
    volumeMin: 1000,
    mktCapMin: 5000,
    liquidityMin: 25000,
    minUniqueTransactions: 10,
    txnCountMin: 50,
    identifier: 'losers',
    filterStables: false,
    sortSearchResultsViaAlgo: false,
    rankingBy: `change${timeFrame}`,
    minAgeMinutes: 60 * 24,
    rankingDirection: 'ASC',
    cacheTime: 1000 * 30
  });
  const {
    recentTokenData: recentItems
  } = useRecentToken();
  const {
    savedTokenData: savedToken,
    loadingSavedTokens,
    handleClearSavedTokens
  } = useSavedToken({});
  const {
    data: SaveTokensQueries
  } = useBasicTokenInfoList(savedToken);
  const resultsToUse = () => {
    switch (mode) {
      case 'search':
        return searchResults;
      case 'memes':
        return subMemeData;
      case 'trending':
        return trendingResults;
      case 'gainers':
        return gainerResults;
      case 'new':
        return newTokens;
      case 'top':
        return topResults;
      case 'losers':
        return loserResults;
      case 'native':
        return nativeTokensData.filter(x => {
          if (x?.token?.symbol == 'ETH' && x.token.networkId != ChainId.ETHEREUM) return false;
          return true;
        }).sort((a, _) => a.token.networkId == ChainId.ETHEREUM ? -1 : a.token.networkId == ChainId.SOLANA ? -0.5 : 1);
      case 'saved':
        return SaveTokensQueries;
      case 'recent':
        if (isMobile) return subMemeData;
        return recentItems;
      default:
        return subMemeData;
    }
  };
  const loadingModes = {
    isLoading,
    subMemeLoading,
    nativeTokensDataLoading,
    gainerLoading,
    topLoading,
    trendingLoading,
    loserLoading,
    newLoading,
    loadingSavedTokens
  };
  useEffect(() => {
    if (resultsToUse()?.length > 0) {
      if (stage !== DefaultStages.results) {
        setStage(DefaultStages.results);
        return;
      }
    } else if (loadingModes.isLoading && mode === 'search' || loadingModes.subMemeLoading && mode === 'memes' || loadingModes.nativeTokensDataLoading && mode === 'native' || loadingModes.gainerLoading && mode === 'gainers' || loadingModes.topLoading && mode === 'top' || loadingModes.trendingLoading && mode === 'trending' || loadingModes.loserLoading && mode === 'losers' || loadingModes.newLoading && mode === 'new') {
      if (stage !== DefaultStages.loading) {
        setStage(DefaultStages.loading);
        return;
      }
    } else {
      if (stage !== DefaultStages.noResults) {
        setStage(DefaultStages.noResults);
        return;
      }
    }
    return () => {};
  }, [stage, resultsToUse, mode, loadingModes]);
  return {
    handleClearSavedTokens,
    DefaultStages,
    showWildWest,
    wildWest,
    handleSearch,
    keyword,
    setKeyword,
    network,
    setNetwork,
    timeFrame,
    setTimeFrame,
    loadingModes,
    resultsToUse,
    useUnprotectedSearch,
    setUseUnprotectedSearch,
    defaultNetwork,
    subMode,
    setSubMode,
    subMemeData,
    mode,
    setMode,
    stage,
    setStage,
    allTokens,
    allTokensLoading,
    recentItems,
    searchBarisFocused,
    setSearchBarIsFocused
  };
};
export const SearchHomeProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const {
    handleClearSavedTokens,
    DefaultStages,
    showWildWest,
    wildWest,
    handleSearch,
    keyword,
    setKeyword,
    network,
    setNetwork,
    timeFrame,
    setTimeFrame,
    loadingModes,
    resultsToUse,
    useUnprotectedSearch,
    setUseUnprotectedSearch,
    defaultNetwork,
    subMode,
    setSubMode,
    subMemeData,
    mode,
    setMode,
    stage,
    setStage,
    allTokens,
    allTokensLoading,
    recentItems,
    searchBarisFocused,
    setSearchBarIsFocused
  } = useSearchHome();
  return <SearchContext.Provider value={{
    handleClearSavedTokens,
    DefaultStages,
    showWildWest,
    wildWest,
    handleSearch,
    keyword,
    setKeyword,
    network,
    setNetwork,
    timeFrame,
    setTimeFrame,
    loadingModes,
    resultsToUse,
    useUnprotectedSearch,
    setUseUnprotectedSearch,
    defaultNetwork,
    subMode,
    setSubMode,
    subMemeData,
    mode,
    setMode,
    stage,
    setStage,
    allTokens,
    allTokensLoading,
    recentItems,
    searchBarisFocused,
    setSearchBarIsFocused
  }} data-sentry-element="unknown" data-sentry-component="SearchHomeProvider" data-sentry-source-file="SearchHomeProvider.tsx">
      {children}
    </SearchContext.Provider>;
};
export const useSearchHomeContext = () => {
  const searchValues = useContext(SearchContext);
  return searchValues;
};